import { useEffect, useMemo, useState } from "react";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import { listFinanceiro } from "../services/financeiroService";
import { tipoFornecedorIcon } from "../services/fornecedoresService";
import StatCard from "../components/ui/StatCard";
import StatusBadge from "../components/ui/StatusBadge";
import Loading, { ErrorBox } from "../components/ui/Loading";
import FinanceiroFormModal from "../components/forms/FinanceiroFormModal";
import { saldoRestante } from "../utils/financeiro";
import { formatMoeda } from "../utils/moeda";

// TipoFinanceiro: 0 Receita · 1 Despesa
const TIPO_RECEITA = 0;
const TIPO_DESPESA = 1;

function formatDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

export default function FinanceiroPage() {
  const { C } = useTheme();
  const { profile } = useAuth();
  // PR3 moeda-por-guia: símbolo único da moeda do guia (post-PR2).
  const moeda = profile?.moeda ?? "EUR";
  const fmt = (v) => formatMoeda(v, moeda);
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  // Ao clicar "+ Nova Receita" ou "+ Nova Despesa" guarda qual botão foi usado
  // pra passar ao modal via tipoInicial (null quando modal abre em modo edição).
  const [novoTipo, setNovoTipo] = useState(null); // "receita" | "despesa" | null
  const [aba, setAba] = useState("todos"); // "todos" | "receitas" | "despesas"

  async function reload() {
    setLoading(true);
    try {
      const res = await listFinanceiro();
      setLista(res);
      setError(null);
    } catch {
      setError("Não foi possível carregar o financeiro.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
  }, []);

  // ─── Totais (mês atual para despesas; total geral para receitas) ───
  // PR3 moeda-por-guia: bucket único — todo Financeiro do guia já é na moeda
  // dele (write-side PR2). Cards renderizam com profile.moeda.
  const totals = useMemo(() => {
    const hoje = new Date();
    const ano = hoje.getFullYear();
    const mes = hoje.getMonth();

    let totalReceitas = 0;
    let aReceber = 0;
    let aPagarMes = 0;
    let countPendentes = 0;

    for (const f of lista) {
      const v = Number(f.valor) || 0;
      if (f.tipo === TIPO_RECEITA) {
        // Total de receitas (histórico completo).
        totalReceitas += v;

        // A receber = saldo restante das receitas.
        const saldo = saldoRestante(f);
        if (saldo > 0) {
          countPendentes++;
          aReceber += saldo;
        }
      } else if (f.tipo === TIPO_DESPESA) {
        // "A pagar no mês" = Σ parcelas de despesa que VENCEM no mês corrente.
        // C2.3: era valor cheio da mãe por criadoEm; agora parcela por vencimento.
        for (const p of f.parcelas ?? []) {
          if (!p.vencimento) continue;
          const [py, pm] = p.vencimento.split("-").map(Number);
          if (py === ano && pm === mes + 1) {
            aPagarMes += Number(p.valor) || 0;
          }
        }
      }
    }

    return { totalReceitas, aReceber, aPagarMes, countPendentes };
  }, [lista]);

  const filtered = useMemo(() => {
    if (aba === "receitas") return lista.filter((f) => f.tipo === TIPO_RECEITA);
    if (aba === "despesas") return lista.filter((f) => f.tipo === TIPO_DESPESA);
    return lista;
  }, [lista, aba]);

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: C.text, margin: 0 }}>
            Financeiro
          </h1>
          <p style={{ color: C.textMuted, fontSize: 13, margin: "4px 0 0" }}>
            Receitas (roteiros e avulsas) e despesas operacionais
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            onClick={() => {
              setEditing(null);
              setNovoTipo("receita");
              setModalOpen(true);
            }}
            style={{
              padding: "10px 20px",
              borderRadius: 10,
              border: "none",
              background: C.green,
              color: "#fff",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            + Nova Receita
          </button>
          <button
            onClick={() => {
              setEditing(null);
              setNovoTipo("despesa");
              setModalOpen(true);
            }}
            style={{
              padding: "10px 20px",
              borderRadius: 10,
              border: "none",
              background: C.red,
              color: "#fff",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            + Nova Despesa
          </button>
        </div>
      </div>

      <FinanceiroFormModal
        open={modalOpen}
        lancamento={editing}
        tipoInicial={novoTipo}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
          setNovoTipo(null);
        }}
        onSaved={reload}
        onDeleted={reload}
      />

      {/* ─── Cards ─── */}
      <div style={{ display: "flex", gap: 14, marginBottom: 22, flexWrap: "wrap" }}>
        <StatCard
          icon="💶"
          label="Total Receitas"
          value={fmt(totals.totalReceitas)}
          sub="Histórico completo"
          color={C.green}
        />
        <StatCard
          icon="⏳"
          label="A Receber"
          value={fmt(totals.aReceber)}
          sub={`${totals.countPendentes} pendente${totals.countPendentes === 1 ? "" : "s"}`}
          color={C.accent}
        />
        <StatCard
          icon="📤"
          label="A pagar no mês"
          value={fmt(totals.aPagarMes)}
          sub="Vence no mês corrente"
          color={C.red}
        />
      </div>

      {/* ─── Abas ─── */}
      <div
        style={{
          display: "flex",
          gap: 2,
          marginBottom: 16,
          background: C.bgCard,
          padding: 4,
          borderRadius: 10,
          border: `1px solid ${C.border}`,
          width: "fit-content",
        }}
      >
        {[
          { id: "todos", label: "Todos", count: lista.length },
          {
            id: "receitas",
            label: "Receitas",
            count: lista.filter((f) => f.tipo === TIPO_RECEITA).length,
          },
          {
            id: "despesas",
            label: "Despesas",
            count: lista.filter((f) => f.tipo === TIPO_DESPESA).length,
          },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setAba(tab.id)}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "none",
              background: aba === tab.id ? C.accent : "transparent",
              color: aba === tab.id ? "#fff" : C.textMuted,
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
              transition: "all 0.15s",
            }}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {loading && <Loading />}
      {error && <ErrorBox message={error} />}

      {!loading && !error && (
        <div
          style={{
            background: C.bgCard,
            borderRadius: 14,
            border: `1px solid ${C.border}`,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "40px 2fr 1fr 1fr 1fr 1fr",
              padding: "12px 20px",
              borderBottom: `1px solid ${C.border}`,
              gap: 8,
            }}
          >
            {["", "Descrição", "Valor", "Saldo", "Vencimento", "Status"].map(
              (h, i) => (
                <span
                  key={i}
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: C.textDim,
                    textTransform: "uppercase",
                    letterSpacing: 0.8,
                  }}
                >
                  {h}
                </span>
              )
            )}
          </div>
          {filtered.map((f) => {
            const isReceita = f.tipo === TIPO_RECEITA;
            const saldo = saldoRestante(f);
            return (
              <div
                key={f.id}
                onClick={() => {
                  setEditing(f);
                  setModalOpen(true);
                }}
                style={{
                  display: "grid",
                  gridTemplateColumns: "40px 2fr 1fr 1fr 1fr 1fr",
                  padding: "14px 20px",
                  borderBottom: `1px solid ${C.border}`,
                  alignItems: "center",
                  cursor: "pointer",
                  gap: 8,
                }}
              >
                {/* Ícone do tipo */}
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    background: isReceita ? C.greenDim : C.redDim,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 14,
                    color: isReceita ? C.green : C.red,
                  }}
                >
                  {isReceita ? "↑" : "↓"}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: C.text,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {isReceita && f.roteiroId && (
                      <span title="Receita de roteiro" style={{ marginRight: 6 }}>
                        🗺️
                      </span>
                    )}
                    {isReceita
                      ? f.roteiroId
                        ? f.clienteNome || f.roteiroTitulo
                        : f.categoria || "Receita avulsa"
                      : f.categoria || "Despesa"}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: C.textMuted,
                      marginTop: 2,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {isReceita ? (
                      f.roteiroId
                        ? f.roteiroTitulo || f.descricao || f.formaPagamento || "—"
                        : f.descricao || f.clienteNome || f.formaPagamento || "—"
                    ) : (
                      <>
                        {f.fornecedorNome && (
                          <span title="Fornecedor" style={{ marginRight: 4 }}>
                            {tipoFornecedorIcon(f.fornecedorTipo)} {f.fornecedorNome}
                          </span>
                        )}
                        {f.fornecedorNome && (f.descricao || f.formaPagamento) && (
                          <span style={{ color: C.textDim }}> · </span>
                        )}
                        {f.descricao || (!f.fornecedorNome && f.formaPagamento) || (!f.fornecedorNome && "—")}
                      </>
                    )}
                  </div>
                </div>
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: C.text,
                  }}
                >
                  {fmt(f.valor)}
                </span>
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: isReceita ? (saldo > 0 ? C.accent : C.green) : C.textMuted,
                  }}
                >
                  {isReceita ? fmt(saldo) : "—"}
                </span>
                <span style={{ fontSize: 12, color: C.textMuted }}>
                  {formatDate(f.vencimento)}
                </span>
                <StatusBadge
                  status={f.status}
                  kind={isReceita ? "financeiro_receita" : "financeiro_despesa"}
                />
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div
              style={{
                padding: 24,
                fontSize: 13,
                color: C.textMuted,
                textAlign: "center",
              }}
            >
              Nenhum lançamento nesta aba.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
